import React, { useState, useEffect, useCallback, useRef } from 'react';
import { recipesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import RecipeCard from '../common/RecipeCard';
import CategoryPicker from '../common/CategoryPicker';
import { FOOD_CATEGORIES, CATEGORY_ICONS } from '../../services/constants';
import './Recipes.css';

export default function HomePage() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const sentinel = useRef(null);
  const initializedRef = useRef(false);

  // Auto-select user's preferred categories on first load
  useEffect(() => {
    if (!initializedRef.current && user?.preferences?.categories?.length > 0) {
      setSelectedCategories(user.preferences.categories.filter(c => FOOD_CATEGORIES.includes(c)));
    }
    initializedRef.current = true;
  }, [user]);

  const fetchRecipes = useCallback(async (pageNum, append) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = { page: pageNum, limit: 12 };
      if (selectedCategories.length > 0) {
        params.categories = selectedCategories.join(',');
      }
      // Filter out recipes containing user's "free from" ingredients
      if (user?.preferences?.avoids?.length > 0) {
        params.avoids = user.preferences.avoids.join(',');
      }
      const data = await recipesAPI.getAll(params);
      setRecipes(prev => append ? [...prev, ...data.recipes] : data.recipes);
      setHasMore(pageNum < data.pages);
    } catch {
      if (!append) setRecipes([]);
    }
    if (append) setLoadingMore(false); else setLoading(false);
  }, [selectedCategories, user?.preferences?.avoids]);

  // Initial load & category change
  useEffect(() => {
    setPage(1);
    fetchRecipes(1, false);
  }, [fetchRecipes]);

  // Load more when page increments beyond 1
  useEffect(() => {
    if (page > 1) fetchRecipes(page, true);
    // eslint-disable-next-line
  }, [page]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          setPage(p => p + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  return (
    <div className="home-page">
      <div className="page-header">
        <h1>Discover Recipes</h1>
        <p>Find your next favorite dish</p>
      </div>

      <CategoryPicker
        items={FOOD_CATEGORIES}
        selected={selectedCategories}
        onToggle={toggleCategory}
        onClear={() => setSelectedCategories([])}
        icons={CATEGORY_ICONS}
      />

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : recipes.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🍽️</span>
          <h3>No recipes found</h3>
          <p>Try different categories or be the first to add one!</p>
        </div>
      ) : (
        <>
          <div className="recipe-grid">
            {recipes.map(recipe => (
              <RecipeCard key={recipe._id} recipe={recipe} />
            ))}
          </div>
          {loadingMore && (
            <div className="loading-center" style={{ padding: '20px 0' }}><div className="spinner" /></div>
          )}
          <div ref={sentinel} className="scroll-sentinel" />
        </>
      )}
    </div>
  );
}
